import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  CalendarClock,
  ClipboardList,
  BarChart3,
  Plus,
} from "lucide-react";

import { useAuth } from "../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../app/permissions";
import { ordersService } from "../../../services/orders";
import { productsService } from "../../../services/products";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { isOpen, ALL_STATUSES } from "../../../utils/orderStatus";
import { formatDateRange } from "../../../utils/dates";
import { greetingKey } from "../../../utils/greeting";
import { CardShell } from "../components/CardShell";
import { QuickActions } from "../components/QuickActions";
import { AvailabilityAlertsWidget } from "../components/AvailabilityAlertsWidget";
import { WarehouseDashboard } from "../components/WarehouseDashboard";

function useDashboardData(isAdmin) {
  const [state, setState] = useState({
    loading: true,
    items: [],
    drafts: [],
    products: [],
  });

  const reload = React.useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    // Admin: aggregate widget counts across ALL users. Standard: own only.
    const ordersCall = isAdmin
      ? ordersService.listAll({ page_size: 100 })
      : ordersService.list({ page_size: 100 });
    const draftsCall = ordersService.list({
      status: "draft",
      page_size: 10,
      sort: "created_at:desc",
    });
    const productsCall = isAdmin
      ? Promise.resolve({ items: [] })
      : productsService.list({ page_size: 100 });
    Promise.all([ordersCall, draftsCall, productsCall])
      .then(([o, d, p]) =>
        setState({
          loading: false,
          items: o.items,
          drafts: d.items,
          products: p.items || [],
        })
      )
      .catch(() =>
        setState({ loading: false, items: [], drafts: [], products: [] })
      );
  }, [isAdmin]);

  useEffect(() => {
    reload();
  }, [reload]);
  return { ...state, reload };
}

function OpenOrdersWidget({ orders, loading }) {
  const { t } = useTranslation();
  const openCount = orders.filter((o) => isOpen(o.status)).length;
  return (
    <CardShell
      title={t("dashboard.widgets.open_orders")}
      icon={ClipboardList}
      testId="widget-open-orders"
    >
      <div className="flex items-baseline gap-3">
        <span
          className="text-5xl font-semibold tracking-tight text-text-primary"
          data-testid="widget-open-orders-count"
        >
          {loading ? "—" : openCount}
        </span>
      </div>
      <p className="mt-1 text-xs text-text-secondary">
        {t("dashboard.widgets.open_orders_body")}
      </p>
      <Link
        to="/orders"
        className="mt-auto inline-flex items-center gap-1 pt-4 text-xs font-medium text-taupe hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne rounded"
        data-testid="widget-open-orders-link"
      >
        {t("dashboard.widgets.view_all_orders")}
        <ArrowUpRight className="h-3 w-3" />
      </Link>
    </CardShell>
  );
}

function UpcomingWidget({ orders, loading }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return orders
      .filter((o) => o.from_date >= today && isOpen(o.status))
      .sort((a, b) => a.from_date.localeCompare(b.from_date))
      .slice(0, 3);
  }, [orders]);

  return (
    <CardShell
      title={t("dashboard.widgets.upcoming")}
      icon={CalendarClock}
      testId="widget-upcoming"
    >
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-3/4" />
        </div>
      ) : upcoming.length === 0 ? (
        <p className="text-xs text-text-secondary">
          {t("dashboard.widgets.upcoming_empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {upcoming.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => navigate(`/orders/${o.id}`)}
                data-testid={`widget-upcoming-${o.id}`}
                className="flex w-full items-center gap-3 rounded-md border border-border-subtle bg-offwhite/40 p-2 text-left transition-colors hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne"
              >
                <span className="font-mono text-xs font-semibold text-text-primary">
                  {o.order_number ? `#${o.order_number}` : ""}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-primary">
                    {o.event_name}
                  </div>
                  <div className="text-[11px] text-text-secondary">
                    {formatDateRange(o.from_date, o.until_date, i18n.language)}
                  </div>
                </div>
                <StatusBadge status={o.status} size="sm" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function statusBarClass(status) {
  switch (status) {
    case "cancelled":
    case "rejected":
      return "bg-status-unavailable";
    case "collection_complete":
    case "delivered":
    case "confirmed":
      return "bg-status-available";
    case "collected":
    case "quality_testing":
      return "bg-status-limited";
    case "shipped":
    case "order_placed":
      return "bg-status-progress";
    case "in_progress":
      return "bg-taupe";
    default:
      return "bg-border-subtle";
  }
}

function StatusOverviewWidget({ orders, loading }) {
  const { t } = useTranslation();
  const counts = useMemo(() => {
    const map = {};
    for (const o of orders) map[o.status] = (map[o.status] || 0) + 1;
    return map;
  }, [orders]);
  const total = orders.length;
  return (
    <CardShell
      title={t("dashboard.widgets.status_overview")}
      icon={BarChart3}
      testId="widget-status-overview"
    >
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-2 w-full rounded-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ) : total === 0 ? (
        <p className="text-xs text-text-secondary">
          {t("dashboard.widgets.status_overview_empty")}
        </p>
      ) : (
        <>
          <div
            className="flex h-2 w-full overflow-hidden rounded-full bg-offwhite"
            data-testid="widget-status-bar"
            role="img"
            aria-label={t("dashboard.widgets.status_overview")}
          >
            {ALL_STATUSES.concat(["cancelled"]).map((s) => {
              const c = counts[s] || 0;
              if (!c) return null;
              const pct = (c / total) * 100;
              return (
                <div
                  key={s}
                  style={{ width: `${pct}%` }}
                  className={statusBarClass(s)}
                  title={`${s}: ${c}`}
                />
              );
            })}
          </div>
          <ul className="mt-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
            {Object.entries(counts)
              .sort((a, b) => b[1] - a[1])
              .map(([s, c]) => (
                <li key={s} className="flex items-center justify-between gap-2">
                  <StatusBadge status={s} size="sm" />
                  <span className="font-mono text-[11px] text-text-secondary">{c}</span>
                </li>
              ))}
          </ul>
        </>
      )}
    </CardShell>
  );
}

function DraftsSection({ drafts, reload }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  if (!drafts || drafts.length === 0) return null;
  const remove = async (id) => {
    try {
      await ordersService.deleteDraft(id);
      reload();
    } catch { /* toast handled elsewhere */ }
  };
  return (
    <section
      className="rounded-lg border border-champagne/40 bg-champagne/5 p-4"
      data-testid="dashboard-drafts"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">
          {t("dashboard.drafts_heading")}
        </h2>
        {drafts.length > 3 && (
          <Link
            to="/orders?scope=drafts"
            className="text-xs text-taupe hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne rounded"
            data-testid="dashboard-drafts-view-all"
          >
            {t("dashboard.view_all_drafts")}
          </Link>
        )}
      </div>
      <ul className="space-y-2">
        {drafts.slice(0, 3).map((d) => (
          <li
            key={d.id}
            data-testid={`dashboard-draft-${d.id}`}
            className="flex flex-col items-start gap-3 rounded-md border border-border-subtle bg-surface p-3 sm:flex-row sm:items-center"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">
                {d.event_name || t("dashboard.untitled_draft")}
              </div>
              <div className="text-xs text-text-secondary">
                {d.from_date && d.until_date
                  ? formatDateRange(d.from_date, d.until_date, i18n.language)
                  : t("dashboard.no_range")}
                {" · "}
                {d.items?.length || 0} {t("wizard.units")}
              </div>
            </div>
            <div className="flex gap-2 self-end sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/orders/${d.id}/edit`)}
                data-testid={`dashboard-draft-continue-${d.id}`}
              >
                {t("dashboard.continue_draft")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(d.id)}
                data-testid={`dashboard-draft-delete-${d.id}`}
                className="text-status-unavailable"
              >
                {t("common.delete")}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = hasPermission(user, Permission.ADMIN_USERS_MANAGE);
  const isWarehouse = user?.role === "warehouse";
  // Admin dashboard fetch is unchanged. Warehouse skips the standard-user
  // fetch entirely (no drafts, no personal products list).
  const { items, drafts, products, loading, reload } = useDashboardData(
    isAdmin && !isWarehouse
  );

  const [gKey, setGKey] = useState(() => greetingKey());
  useEffect(() => {
    const id = setInterval(() => setGKey(greetingKey()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1
            className="text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl"
            data-testid="dashboard-welcome"
          >
            {t(gKey, { name: user?.name || "" })}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-secondary">
            {isWarehouse
              ? t("warehouse_dashboard.welcome_body")
              : t("dashboard.welcome_body")}
          </p>
        </div>
        {!isAdmin && !isWarehouse && (
          <Button
            onClick={() => navigate("/orders/new")}
            data-testid="dashboard-create-order"
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-2 focus-visible:ring-champagne"
            size="lg"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("dashboard.create_order_cta")}
          </Button>
        )}
      </header>

      {isWarehouse ? (
        <WarehouseDashboard />
      ) : (
        <>
          {isAdmin && <QuickActions />}

          <DraftsSection drafts={drafts} reload={reload} />

          {isAdmin ? (
            <section
              className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              data-testid="dashboard-widget-grid"
            >
              <OpenOrdersWidget orders={items} loading={loading} />
              <UpcomingWidget orders={items} loading={loading} />
              <StatusOverviewWidget orders={items} loading={loading} />
            </section>
          ) : (
            <>
              <section
                className="grid gap-4 grid-cols-1 lg:grid-cols-2"
                data-testid="dashboard-grid-row1"
              >
                <OpenOrdersWidget orders={items} loading={loading} />
                <AvailabilityAlertsWidget products={products} />
              </section>
              <section
                className="grid gap-4 grid-cols-1 lg:grid-cols-2"
                data-testid="dashboard-grid-row2"
              >
                <UpcomingWidget orders={items} loading={loading} />
                <StatusOverviewWidget orders={items} loading={loading} />
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}
